import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import {
  getMyDevices,
  deleteDevice,
  uploadBackgroundImage,
  getBackgroundImage,
  getAllBackgroundImages,
  setBackgroundImage,
  deleteBackgroundImage,
  createUser,
  CreateUserData,
  importUsers,
  ImportResult,
  BackgroundImage,
  deleteUser,
  getAllUsers,
  testEmail,
  promoteToManager,
  getPendingApprovals,
  approveUser,
  rejectUser
} from '../../api/userApi';
import { useAppSelector } from '../../hooks/redux';
import { ThemeCustomizer } from './ThemeCustomizer';
import { UserThemeCustomizer } from '../user/UserThemeCustomizer';
import { validateImageFile } from '../../utils/imageCompression';
import { User } from '../../types';

interface AdminSettingsProps {
  onBackgroundUpdate?: (url: string) => void;
}

export const AdminSettings: React.FC<AdminSettingsProps> = ({ onBackgroundUpdate }) => {
  const { loginUser } = useAppSelector((state) => state.user);
  const [brandPart1, setBrandPart1] = useState('BROOD');
  const [brandPart2, setBrandPart2] = useState('book');
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);
  const [brandShowBlock, setBrandShowBlock] = useState(true);
  const [showAuthLinks, setShowAuthLinks] = useState(false);
  const [brandLogoUploading, setBrandLogoUploading] = useState(false);
  const brandLogoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    axios.get('/api/auth/branding', { withCredentials: true })
      .then(res => {
        if (res.data.success) {
          const d = res.data.data;
          setBrandPart1(d.part1 || 'BROOD');
          setBrandPart2(d.part2 || 'book');
          setBrandLogoUrl(d.logoUrl || null);
          setBrandShowBlock(d.showBlock !== false);
          setShowAuthLinks(d.showAuthLinks === true);
        }
      })
      .catch(() => {});
  }, []);

  const saveSetting = async (key: string, value: string) => {
    await axios.post('/api/auth/admin/settings', { setting_key: key, setting_value: value }, { withCredentials: true });
  };

  const handleSaveBrandName = async () => {
    try {
      await saveSetting('brand_name', JSON.stringify({ part1: brandPart1 || 'BROOD', part2: brandPart2 || 'book' }));
      window.dispatchEvent(new Event('brand-name-changed'));
      setMessage({ type: 'success', text: 'Brand name saved' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save brand name' });
    }
  };

  const handleResetBrandName = async () => {
    setBrandPart1('BROOD');
    setBrandPart2('book');
    try {
      await saveSetting('brand_name', JSON.stringify({ part1: 'BROOD', part2: 'book' }));
      window.dispatchEvent(new Event('brand-name-changed'));
      setMessage({ type: 'success', text: 'Brand name reset to default' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to reset brand name' });
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBrandLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await axios.post('/api/auth/admin/upload-logo', formData, { withCredentials: true });
      if (res.data.success) {
        setBrandLogoUrl(res.data.data.logoUrl);
        window.dispatchEvent(new Event('brand-name-changed'));
        setMessage({ type: 'success', text: 'Logo uploaded' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to upload logo' });
    } finally {
      setBrandLogoUploading(false);
      if (brandLogoInputRef.current) brandLogoInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    setBrandLogoUrl(null);
    try {
      await saveSetting('brand_logo_url', '');
      window.dispatchEvent(new Event('brand-name-changed'));
      setMessage({ type: 'success', text: 'Logo removed' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to remove logo' });
    }
  };

  const handleToggleShowBlock = async (show: boolean) => {
    setBrandShowBlock(show);
    try {
      await saveSetting('brand_show_block', show ? 'true' : 'false');
      window.dispatchEvent(new Event('brand-name-changed'));
    } catch {
      setMessage({ type: 'error', text: 'Failed to save setting' });
    }
  };

  const handleToggleAuthLinks = async (show: boolean) => {
    setShowAuthLinks(show);
    try {
      await saveSetting('show_auth_links', show ? 'true' : 'false');
      window.dispatchEvent(new Event('brand-name-changed'));
    } catch {
      setMessage({ type: 'error', text: 'Failed to save setting' });
    }
  };

  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [currentBackground, setCurrentBackground] = useState<string>('');
  const [backgroundImages, setBackgroundImages] = useState<BackgroundImage[]>([]);
  const [showGallery, setShowGallery] = useState(false);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  // Create user dialog state
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // User management state
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Email testing state
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [neverLoggedInUsers, setNeverLoggedInUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Message management state
  const [messageExpirationHours, setMessageExpirationHours] = useState<string>('720');
  const [conversationTrashDays, setConversationTrashDays] = useState<string>('30');
  const [savingMessageSettings, setSavingMessageSettings] = useState(false);
  const [deletingExpiredMessages, setDeletingExpiredMessages] = useState(false);

  // Trash management state
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [trashedConversations, setTrashedConversations] = useState<any[]>([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [newUser, setNewUser] = useState<CreateUserData>({
    email: '',
    displayName: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    occupation: '',
    bio: '',
    photoURL: '',
    birthday: '',
    isLocationVisible: false,
    isBirthdayVisible: false,
  });

  // Device management state
  const [devices, setDevices] = useState<any[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [showDevicesModal, setShowDevicesModal] = useState(false);

  // Pending approvals state
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);

  // Password change state
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Check if user is admin (ID 1) or manager (ID 2) - convert to string for comparison
  const userId = String(loginUser?.id || '');
  const isAdminOrManager = userId === '1' || userId === '2';
  const isAdmin = userId === '1';

  // Component render tracking removed to prevent re-render loops

  // Auto-dismiss message after 10 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [message]);

  // Lock body scroll when user management modal is open
  useEffect(() => {
    if (showUserManagement) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow || '';
      // Lock scroll
      document.body.style.overflow = 'hidden';

      // Cleanup function to restore scroll when modal closes
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showUserManagement]);

  React.useEffect(() => {
    // Only load background if user is admin/manager to prevent unnecessary API calls
    if (!isAdminOrManager) return;

    // Load current background
    const loadCurrentBackground = async () => {
      try {
        const response = await getBackgroundImage();
        if (response.data?.data?.backgroundUrl) {
          setCurrentBackground(response.data.data.backgroundUrl);
        }
      } catch (error) {
        console.error('Error loading current background:', error);
        // Don't set error state to prevent re-renders, just log
      }
    };

    loadCurrentBackground();
  }, [isAdminOrManager]);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!validateImageFile(file)) {
      setMessage({ type: 'error', text: 'Please select a valid image file (JPEG, PNG, or WebP)' });
      return;
    }

    // Validate file size (20MB limit before compression)
    if (file.size > 20 * 1024 * 1024) {
      setMessage({ type: 'error', text: `File size is ${formatFileSize(file.size)}. Please select a file smaller than 20MB` });
      return;
    }

    console.log(`Background upload started: ${file.name} (${formatFileSize(file.size)})`);
    setUploading(true);
    setMessage(null);

    try {
      const response = await uploadBackgroundImage(file);

      if (response.data?.success && response.data.data) {
        const newBackgroundUrl = response.data.data.backgroundUrl;
        setCurrentBackground(newBackgroundUrl);
        setMessage({ type: 'success', text: 'Background image updated successfully!' });

        // Apply background system-wide immediately (newBackgroundUrl is now the full GCS URL)
        document.body.style.backgroundImage = `url(${newBackgroundUrl})`;
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center center';
        document.body.style.backgroundAttachment = 'scroll';
        document.body.style.backgroundRepeat = 'repeat-x';
        document.body.style.backgroundColor = 'black';
        document.body.style.animation = 'moveBackground 60s linear infinite';

        // Force multiple repaints to ensure the background updates immediately
        void document.body.offsetHeight;
        setTimeout(() => {
          void document.body.offsetHeight;
          document.body.style.backgroundImage = `url(${newBackgroundUrl})`;
        }, 10);

        // Save URL to localStorage for instant loading on refresh
        localStorage.setItem('tapestry-background', newBackgroundUrl);
        console.log('AdminSettings: Applied uploaded background:', newBackgroundUrl);

        // Call callback to update parent components
        onBackgroundUpdate?.(newBackgroundUrl);

        // If gallery is open, refresh it to show the new background
        if (showGallery) {
          await loadBackgroundGallery();
        }
      } else {
        setMessage({ type: 'error', text: response.data?.message || 'Upload failed' });
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Upload failed. Please try again.'
      });
    } finally {
      setUploading(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Background gallery functions
  const loadBackgroundGallery = async () => {
    setLoadingGallery(true);
    try {
      console.log('AdminSettings: Loading background gallery...');
      const response = await getAllBackgroundImages();
      console.log('AdminSettings: Gallery API response:', response);

      if (response.data?.success && response.data.data) {
        console.log('AdminSettings: Found backgrounds:', response.data.data.backgrounds);
        // Debug the URLs being generated
        response.data.data.backgrounds.forEach(bg => {
          console.log('AdminSettings: Background URL:', bg.url, 'Full URL:', window.location.origin + bg.url);
        });
        setBackgroundImages(response.data.data.backgrounds);
      } else {
        console.warn('AdminSettings: No background data in response:', response.data);
        setBackgroundImages([]);
      }
    } catch (error) {
      console.error('AdminSettings: Failed to load background gallery:', error);
      setMessage({ type: 'error', text: 'Failed to load background gallery' });
      setBackgroundImages([]);
    } finally {
      setLoadingGallery(false);
    }
  };

  const handleShowGallery = () => {
    setShowGallery(true);
    loadBackgroundGallery();
  };

  const handleSelectBackground = async (backgroundUrl: string) => {
    try {
      const response = await setBackgroundImage(backgroundUrl);
      if (response.data?.success) {
        setCurrentBackground(backgroundUrl);
        setMessage({ type: 'success', text: 'Background updated successfully' });

        // Apply background system-wide immediately (backgroundUrl is now the full GCS URL)
        document.body.style.backgroundImage = `url(${backgroundUrl})`;
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center center';
        document.body.style.backgroundAttachment = 'scroll';
        document.body.style.backgroundRepeat = 'repeat-x';
        document.body.style.backgroundColor = 'black';
        document.body.style.animation = 'moveBackground 60s linear infinite';

        // Force multiple repaints to ensure the background updates immediately
        void document.body.offsetHeight;
        setTimeout(() => {
          void document.body.offsetHeight;
          document.body.style.backgroundImage = `url(${backgroundUrl})`;
        }, 10);

        // Save URL to localStorage for instant loading on refresh
        localStorage.setItem('tapestry-background', backgroundUrl);

        console.log('AdminSettings: Applied background:', backgroundUrl);

        onBackgroundUpdate?.(backgroundUrl);
        // Refresh gallery to update current indicators
        await loadBackgroundGallery();
      }
    } catch (error) {
      console.error('Failed to set background:', error);
      setMessage({ type: 'error', text: 'Failed to update background' });
    }
  };

  const handleDeleteBackground = async (filePath: string) => {
    if (!window.confirm('Are you sure you want to delete this background image?')) {
      return;
    }

    try {
      const response = await deleteBackgroundImage(filePath);
      if (response.data?.success) {
        setMessage({ type: 'success', text: 'Background deleted successfully' });
        // Refresh gallery
        await loadBackgroundGallery();
      }
    } catch (error: any) {
      console.error('Failed to delete background:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to delete background'
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Create user functions
  const handleCreateUserOpen = () => {
    setCreateUserDialog(true);
    setMessage(null);
  };

  const handleCreateUserClose = () => {
    setCreateUserDialog(false);
    setNewUser({
      email: '',
      displayName: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      occupation: '',
      bio: '',
      photoURL: '',
      birthday: '',
      isLocationVisible: false,
      isBirthdayVisible: false,
    });
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.displayName) {
      setMessage({ type: 'error', text: 'Email and display name are required' });
      return;
    }

    setCreatingUser(true);
    setMessage(null);

    try {
      const response = await createUser(newUser);

      if (response.data?.success) {
        setMessage({ type: 'success', text: 'User created successfully! Welcome email sent.' });
        handleCreateUserClose();
      } else {
        setMessage({ type: 'error', text: response.data?.message || 'Failed to create user' });
      }
    } catch (error: any) {
      console.error('Create user error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to create user. Please try again.'
      });
    } finally {
      setCreatingUser(false);
    }
  };

  // CSV Import functions
  const handleCsvFileSelect = () => {
    csvFileInputRef.current?.click();
  };

  const handleDownloadSampleCSV = () => {
    // Create sample CSV data with headers and example rows
    const sampleData = [
      ['firstName', 'lastName', 'email', 'displayName', 'phoneNumber', 'occupation', 'bio', 'birthday', 'isBirthdayVisible'],
      ['John', 'Doe', 'john.doe@example.com', 'John D', '+1234567890', 'Software Engineer', 'Love coding and family time', '1985-03-15', 'true'],
      ['Jane', 'Smith', 'jane.smith@example.com', '', '+1987654321', 'Teacher', 'Passionate about education', '1990-07-22', 'true'],
      ['Michael', 'Wilson', '', 'Mike W', '+1555000123', 'Doctor', 'Family physician and outdoor enthusiast', '1978-12-08', 'false'],
      ['Sarah', 'Johnson', '', '', '', 'Artist', 'Loves painting and family gatherings', '1992-05-20', 'true']
    ];

    // Convert to CSV format
    const csvContent = sampleData.map(row =>
      row.map(field => {
        // Escape fields that contain commas, quotes, or newlines
        if (field.includes(',') || field.includes('"') || field.includes('\n')) {
          return '"' + field.replace(/"/g, '""') + '"';
        }
        return field;
      }).join(',')
    ).join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'tapestry-users-sample.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setMessage({ type: 'error', text: 'Please select a CSV file' });
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 5MB' });
      return;
    }

    setCsvImporting(true);
    setMessage(null);
    setImportResult(null);

    try {
      const response = await importUsers(file);

      if (response.data?.success && response.data.data) {
        const result = response.data.data;
        setImportResult(result);
        setMessage({
          type: 'success',
          text: `Import completed! ${result.imported} users imported, ${result.skipped} skipped.`
        });
      } else {
        setMessage({ type: 'error', text: response.data?.message || 'Import failed' });
      }
    } catch (error: any) {
      console.error('CSV import error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Import failed. Please try again.'
      });
    } finally {
      setCsvImporting(false);
      // Clear file input
      if (csvFileInputRef.current) {
        csvFileInputRef.current.value = '';
      }
    }
  };

  // User Management Functions
  const loadAllUsers = async () => {
    console.log('AdminSettings: loadAllUsers called');
    setLoadingUsers(true);
    try {
      const response = await getAllUsers();
      if (response.data?.success && response.data.data?.users) {
        console.log('AdminSettings: Loaded users:', response.data.data.users.length);
        setAllUsers(response.data.data.users);
      } else {
        console.error('AdminSettings: Failed to load users');
        setMessage({ type: 'error', text: 'Failed to load users' });
      }
    } catch (error: any) {
      console.error('Load users error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load users'
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleShowUserManagement = () => {
    setShowUserManagement(true);
    setUserSearchTerm(''); // Clear search when opening modal
    setMessage(null); // Clear any previous messages
    loadAllUsers();
  };

  const handleCloseUserManagement = () => {
    setShowUserManagement(false);
    setUserSearchTerm(''); // Clear search when closing modal
    setMessage(null); // Clear messages when closing
  };

  const handleDeleteUser = async (userToDelete: User) => {
    const userDisplay = userToDelete.displayName || userToDelete.display_name || userToDelete.email;

    if (!window.confirm(`Are you sure you want to delete user "${userDisplay}"? This action cannot be undone.`)) {
      return;
    }

    console.log('AdminSettings: Attempting to delete user:', userToDelete.id);
    setMessage(null); // Clear previous messages

    try {
      const response = await deleteUser(Number(userToDelete.id));
      console.log('AdminSettings: Delete user response:', response);

      if (response.data?.success) {
        setMessage({ type: 'success', text: response.data.message || 'User deleted successfully' });
        console.log('AdminSettings: User deleted successfully, refreshing list...');
        // Refresh the user list without closing the modal
        await loadAllUsers();
        console.log('AdminSettings: User list refreshed');
      } else {
        console.error('AdminSettings: Delete failed with response:', response.data);
        setMessage({ type: 'error', text: response.data?.message || 'Failed to delete user' });
      }
    } catch (error: any) {
      console.error('AdminSettings: Delete user error:', error);
      console.error('AdminSettings: Error response:', error.response);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Failed to delete user'
      });
    }
  };

  const handlePromoteToManager = async (userToPromote: User) => {
    const userDisplay = userToPromote.displayName || userToPromote.display_name || userToPromote.email;

    if (!window.confirm(`Are you sure you want to promote "${userDisplay}" to Manager? This will give them administrative privileges.`)) {
      return;
    }

    console.log('AdminSettings: Attempting to promote user to manager:', userToPromote.id);
    setMessage(null);

    try {
      const response = await promoteToManager(Number(userToPromote.id));
      console.log('AdminSettings: Promote to manager response:', response);

      if (response.data?.success) {
        setMessage({ type: 'success', text: response.data.message || 'User promoted to manager successfully' });
        console.log('AdminSettings: User promoted successfully, refreshing list...');
        await loadAllUsers();
        console.log('AdminSettings: User list refreshed');
      } else {
        console.error('AdminSettings: Promotion failed with response:', response.data);
        setMessage({ type: 'error', text: response.data?.message || 'Failed to promote user' });
      }
    } catch (error: any) {
      console.error('AdminSettings: Promote to manager error:', error);
      console.error('AdminSettings: Error response:', error.response);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Failed to promote user to manager'
      });
    }
  };

  // Pending Approval Functions
  const loadPendingApprovals = async () => {
    setLoadingPending(true);
    try {
      const response = await getPendingApprovals();
      if (response.data?.success && response.data.data?.users) {
        setPendingUsers(response.data.data.users);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load pending approvals' });
    } finally {
      setLoadingPending(false);
    }
  };

  const handleShowPendingModal = () => {
    setShowPendingModal(true);
    loadPendingApprovals();
  };

  const handleApproveUser = async (user: User) => {
    try {
      const response = await approveUser(Number(user.id));
      if (response.data?.success) {
        setMessage({ type: 'success', text: response.data.message || 'User approved' });
        await loadPendingApprovals();
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to approve user' });
    }
  };

  const handleRejectUser = async (user: User) => {
    const userDisplay = user.display_name || user.email;
    if (!window.confirm(`Reject "${userDisplay}"? They will be notified by email.`)) return;
    try {
      const response = await rejectUser(Number(user.id));
      if (response.data?.success) {
        setMessage({ type: 'success', text: response.data.message || 'User rejected' });
        await loadPendingApprovals();
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to reject user' });
    }
  };

  // Device Management Functions
  const handleShowDevices = async () => {
    setShowDevicesModal(true);
    setLoadingDevices(true);
    try {
      const response = await getMyDevices();
      if (response.data?.success) {
        setDevices(response.data.data?.devices || []);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to load devices' });
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleDeleteDevice = async (deviceId: string, deviceName: string) => {
    if (!window.confirm(`Remove "${deviceName}"? This device will need to log in again.`)) return;
    try {
      await deleteDevice(deviceId);
      setDevices(prev => prev.filter(d => d.device_id !== deviceId));
      setMessage({ type: 'success', text: `${deviceName} removed` });
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to remove device' });
    }
  };

  // Email Testing Functions
  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      setMessage({ type: 'error', text: 'Please enter an email address to test' });
      return;
    }

    setTestingEmail(true);
    setMessage(null);

    try {
      const response = await testEmail(testEmailAddress);
      if (response.data?.success) {
        setMessage({
          type: 'success',
          text: `Test email sent successfully to ${testEmailAddress}. Check your inbox (and spam folder).`
        });
      } else {
        setMessage({
          type: 'error',
          text: response.data?.message || 'Failed to send test email'
        });
      }
    } catch (error: any) {
      console.error('Test email error:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to send test email. Check server logs for details.';
      setMessage({
        type: 'error',
        text: errorMsg
      });
    } finally {
      setTestingEmail(false);
    }
  };

  const handleOpenReminderModal = async () => {
    setMessage(null);
    try {
      // Fetch users who have never logged in
      const response = await fetch('/api/auth/admin/users-never-logged-in', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setNeverLoggedInUsers(data.data.users);
        // Select all by default
        setSelectedUserIds(new Set(data.data.users.map((u: User) => u.id)));
        setShowReminderModal(true);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to load users' });
      }
    } catch (error: any) {
      console.error('Load never logged in users error:', error);
      setMessage({ type: 'error', text: 'Failed to load users' });
    }
  };

  const handleCloseReminderModal = () => {
    setShowReminderModal(false);
    setSelectedUserIds(new Set());
  };

  // Lock body scroll when reminder modal is open
  useEffect(() => {
    if (showReminderModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showReminderModal]);

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedUserIds(new Set(neverLoggedInUsers.map(u => u.id)));
  };

  const handleDeselectAll = () => {
    setSelectedUserIds(new Set());
  };

  const handleSendFirstLoginReminders = async () => {
    if (selectedUserIds.size === 0) {
      setMessage({ type: 'error', text: 'Please select at least one user' });
      return;
    }

    if (!window.confirm(`Send reminder emails to ${selectedUserIds.size} selected user(s)?`)) {
      return;
    }

    setSendingReminders(true);
    setMessage(null);

    try {
      const response = await fetch('/api/auth/admin/send-first-login-reminders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userIds: Array.from(selectedUserIds)
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: 'success',
          text: `${data.data.emailsSent} reminder email(s) sent successfully.`
        });
        handleCloseReminderModal();
      } else {
        setMessage({
          type: 'error',
          text: data.message || 'Failed to send reminder emails'
        });
      }
    } catch (error: any) {
      console.error('Send first login reminders error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send reminder emails. Check server logs for details.'
      });
    } finally {
      setSendingReminders(false);
    }
  };

  // Message management functions
  const handleSaveMessageSettings = async () => {
    setSavingMessageSettings(true);
    setMessage(null);

    try {
      // Save message expiration days
      const response1 = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/auth/admin/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          setting_key: 'message_expiration_hours',
          setting_value: messageExpirationHours
        })
      });

      const data1 = await response1.json();

      // Save conversation trash days
      const response2 = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/auth/admin/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          setting_key: 'conversation_trash_days',
          setting_value: conversationTrashDays
        })
      });

      const data2 = await response2.json();

      if (data1.success && data2.success) {
        setMessage({
          type: 'success',
          text: `Settings saved: Message expiration ${messageExpirationHours} hours, Trash cleanup ${conversationTrashDays} days`
        });
      } else {
        setMessage({
          type: 'error',
          text: data1.message || data2.message || 'Failed to save message settings'
        });
      }
    } catch (error: any) {
      console.error('Save message settings error:', error);
      setMessage({
        type: 'error',
        text: 'Failed to save message settings'
      });
    } finally {
      setSavingMessageSettings(false);
    }
  };

  const handleLoadTrash = async () => {
    setLoadingTrash(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/messages/trash`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setTrashedConversations(data.data.conversations || []);
        setShowTrashModal(true);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to load trash' });
      }
    } catch (error) {
      console.error('Load trash error:', error);
      setMessage({ type: 'error', text: 'Failed to load trash' });
    } finally {
      setLoadingTrash(false);
    }
  };

  const handleRestoreConversation = async (id: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/messages/trash/${id}/restore`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Conversation restored' });
        handleLoadTrash(); // Reload trash
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to restore conversation' });
      }
    } catch (error) {
      console.error('Restore conversation error:', error);
      setMessage({ type: 'error', text: 'Failed to restore conversation' });
    }
  };

  const handleCleanupOldTrash = async () => {
    if (!window.confirm(`Delete all conversations in trash older than ${conversationTrashDays} days? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/messages/trash/cleanup`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: `Deleted ${data.data.deletedCount} old conversations` });
        handleLoadTrash(); // Reload trash
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to cleanup trash' });
      }
    } catch (error) {
      console.error('Cleanup trash error:', error);
      setMessage({ type: 'error', text: 'Failed to cleanup trash' });
    }
  };

  const handleDeleteExpiredMessages = async () => {
    if (!window.confirm('Are you sure you want to delete all expired messages? This cannot be undone.')) {
      return;
    }

    setDeletingExpiredMessages(true);
    setMessage(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/messages/expired`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: 'success',
          text: data.message || 'Expired messages deleted successfully'
        });
      } else {
        setMessage({
          type: 'error',
          text: data.message || 'Failed to delete expired messages'
        });
      }
    } catch (error: any) {
      console.error('Delete expired messages error:', error);
      setMessage({
        type: 'error',
        text: 'Failed to delete expired messages'
      });
    } finally {
      setDeletingExpiredMessages(false);
    }
  };

  // Load message expiration and trash cleanup settings
  React.useEffect(() => {
    if (!isAdmin) return;

    const loadMessageSettings = async () => {
      try {
        // Load message expiration days
        const response1 = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/auth/admin/settings/message_expiration_hours`, {
          credentials: 'include'
        });
        const data1 = await response1.json();
        if (data1.success && data1.data) {
          setMessageExpirationHours(data1.data.setting_value || '720');
        }

        // Load conversation trash days
        const response2 = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/auth/admin/settings/conversation_trash_days`, {
          credentials: 'include'
        });
        const data2 = await response2.json();
        if (data2.success && data2.data) {
          setConversationTrashDays(data2.data.setting_value || '30');
        }
      } catch (error) {
        console.error('Failed to load message settings:', error);
      }
    };

    loadMessageSettings();
  }, [isAdmin]);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    setChangingPassword(true);
    setMessage(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setShowPasswordChangeModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to change password' });
      }
    } catch (error: any) {
      console.error('Change password error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to change password'
      });
    } finally {
      setChangingPassword(false);
    }
  };


  return (
    <div className="p-3" style={{ position: 'relative' }}>
      {/* Global Message Alert - Fixed at top */}
      {message && (
        <div
          className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'}`}
          style={{
            position: 'fixed',
            top: '70px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: '300px',
            maxWidth: '90vw',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            animation: 'slideDown 0.3s ease-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <style>{`
            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateX(-50%) translateY(-20px);
              }
              to {
                opacity: 1;
                transform: translateX(-50%) translateY(0);
              }
            }
          `}</style>
          <span style={{ flex: 1 }}>{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
            title="Dismiss"
          >
            <span className="material-icons" style={{ fontSize: '18px' }}>close</span>
          </button>
        </div>
      )}

      <h1 className="h4 mb-3">{isAdminOrManager ? (isAdmin ? 'Admin' : 'Manager') : 'User'} Settings</h1>

      {/* Refresh App Section - Available to ALL users */}
      <div className="card glassCard mb-3">
        <div className="card-content">
          <div className="flex flex-align-center mb-2">
            <span className="material-icons mr-1">refresh</span>
            <h3 className="h6 mb-0">Refresh App</h3>
          </div>
          <p className="text-secondary mb-2">
            Hard refresh the application to load the latest updates. Useful for iPhone/iPad users where pull-to-refresh may not work.
          </p>
          <button
            className="btn btn-contained"
            onClick={() => window.location.reload()}
          >
            <span className="material-icons mr-1">refresh</span>
            Refresh Now
          </button>
        </div>
      </div>

      {/* Change Password Section - Available to ALL users */}
      <div className="card glassCard mb-3">
        <div className="card-content">
          <div className="flex flex-align-center mb-2">
            <span className="material-icons mr-1">lock</span>
            <h3 className="h6 mb-0">Change Password</h3>
          </div>
          <p className="text-secondary mb-2">
            Update your account password. Requires your current password for security.
          </p>
          <button
            className="btn btn-contained"
            onClick={() => setShowPasswordChangeModal(true)}
          >
            <span className="material-icons mr-1">vpn_key</span>
            Change Password
          </button>
        </div>
      </div>

      {/* Device Management - Available to ALL users */}
      <div className="card glassCard mb-3">
        <div className="card-content">
          <div className="flex flex-align-center mb-2">
            <span className="material-icons mr-1">devices</span>
            <h3 className="h6 mb-0">My Devices</h3>
          </div>
          <p className="text-secondary mb-2">
            View and remove devices that have access to your account.
          </p>
        </div>
        <div className="card-actions">
          <button className="btn btn-outlined" onClick={handleShowDevices} disabled={loadingDevices}>
            <span className="material-icons mr-1">{loadingDevices ? 'hourglass_empty' : 'phone_android'}</span>
            {loadingDevices ? 'Loading...' : 'Manage Devices'}
          </button>
        </div>
      </div>

      {/* Pending Approvals Section - Available to Admin/Manager */}
      {isAdminOrManager && (
        <div className="card glassCard mb-3">
          <div className="card-content">
            <div className="flex flex-align-center mb-2">
              <span className="material-icons mr-1">how_to_reg</span>
              <h3 className="h6 mb-0">Member Approvals</h3>
            </div>
            <p className="text-secondary mb-2">
              Review and approve or reject new member registrations.
            </p>
          </div>
          <div className="card-actions">
            <button
              className="btn btn-outlined"
              onClick={handleShowPendingModal}
              disabled={loadingPending}
            >
              <span className="material-icons mr-1">
                {loadingPending ? 'hourglass_empty' : 'pending_actions'}
              </span>
              {loadingPending ? 'Loading...' : 'Review Pending Members'}
            </button>
          </div>
        </div>
      )}

      {/* User Management Section - Available to Admin only */}
      {isAdmin && (
        <div className="card glassCard mb-3">
          <div className="card-content">
            <div className="flex flex-align-center mb-2">
              <span className="material-icons mr-1">manage_accounts</span>
              <h3 className="h6 mb-0">User Management</h3>
            </div>

            <p className="text-secondary mb-2">
              View and manage all user accounts. Admin can delete users (except themselves).
            </p>
          </div>

          <div className="card-actions">
            <button
              className="btn btn-outlined"
              onClick={handleShowUserManagement}
              disabled={loadingUsers}
            >
              <span className="material-icons mr-1">
                {loadingUsers ? 'hourglass_empty' : 'people'}
              </span>
              {loadingUsers ? 'Loading...' : 'Manage Users'}
            </button>
          </div>
        </div>
      )}

      {/* Brand / White Label Editor - Admin and Manager Only */}
      {isAdminOrManager && (
        <div className="card glassCard mb-3">
          <div className="card-content">
            <div className="flex flex-align-center mb-2">
              <span className="material-icons mr-1">branding_watermark</span>
              <h3 className="h6 mb-0">Branding</h3>
            </div>
            <p className="text-secondary mb-3">
              Customize the logo and name shown in the header and login screen. Toggle off to hide the brand block entirely for white-label demos.
            </p>

            {/* Show/hide toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <span style={{ fontWeight: 500 }}>Show brand block</span>
              <button
                onClick={() => handleToggleShowBlock(!brandShowBlock)}
                style={{
                  width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', padding: 0,
                  background: brandShowBlock ? 'var(--primary-color)' : 'var(--border-color)',
                  position: 'relative', transition: 'background 0.2s'
                }}
              >
                <span style={{
                  position: 'absolute', top: '3px', left: brandShowBlock ? '23px' : '3px',
                  width: '18px', height: '18px', borderRadius: '50%', background: 'white',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </button>
              <span className="text-secondary" style={{ fontSize: '13px' }}>
                {brandShowBlock ? 'Visible' : 'Hidden (white-label mode)'}
              </span>
            </div>

            {/* Register / Reset password links toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <span style={{ fontWeight: 500 }}>Show register &amp; reset password links</span>
              <button
                onClick={() => handleToggleAuthLinks(!showAuthLinks)}
                style={{
                  width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', padding: 0,
                  background: showAuthLinks ? 'var(--primary-color)' : 'var(--border-color)',
                  position: 'relative', transition: 'background 0.2s'
                }}
              >
                <span style={{
                  position: 'absolute', top: '3px', left: showAuthLinks ? '23px' : '3px',
                  width: '18px', height: '18px', borderRadius: '50%', background: 'white',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </button>
              <span className="text-secondary" style={{ fontSize: '13px' }}>
                {showAuthLinks ? 'Visible on login screen' : 'Hidden (default)'}
              </span>
            </div>

            {/* Logo upload */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: 500, marginBottom: '8px' }}>Logo</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                {brandLogoUrl
                  ? <img src={brandLogoUrl} alt="Brand logo" style={{ height: '40px', borderRadius: '4px', border: '1px solid var(--border-color)' }} />
                  : <div style={{ height: '40px', width: '40px', borderRadius: '4px', border: '1px dashed var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span className="material-icons" style={{ color: 'var(--text-secondary)', fontSize: '20px' }}>image</span>
                    </div>
                }
                <input ref={brandLogoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
                <button className="btn btn-outlined btn-sm" onClick={() => brandLogoInputRef.current?.click()} disabled={brandLogoUploading}>
                  {brandLogoUploading ? 'Uploading…' : 'Upload Logo'}
                </button>
                {brandLogoUrl && (
                  <button className="btn btn-outlined btn-sm" style={{ color: 'var(--error-color)' }} onClick={handleRemoveLogo}>
                    Remove
                  </button>
                )}
              </div>
            </div>

            {/* Name fields */}
            <div style={{ fontWeight: 500, marginBottom: '8px' }}>Brand name</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={brandPart1}
                onChange={(e) => setBrandPart1(e.target.value)}
                placeholder="BROOD"
                style={{ width: '140px', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '16px', fontWeight: 'bold', color: '#3a9397', background: 'var(--background-paper)' }}
              />
              <input
                type="text"
                value={brandPart2}
                onChange={(e) => setBrandPart2(e.target.value)}
                placeholder="book"
                style={{ width: '140px', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '16px', fontWeight: 'bold', color: '#ff9800', background: 'var(--background-paper)' }}
              />
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                <span style={{ color: '#3a9397' }}>{brandPart1 || 'BROOD'}</span>
                <span style={{ color: '#ff9800' }}>{brandPart2 || 'book'}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-contained btn-sm" onClick={handleSaveBrandName}>Save Name</button>
              <button className="btn btn-outlined btn-sm" onClick={handleResetBrandName}>Reset to Default</button>
            </div>
          </div>
        </div>
      )}

      {/* App Theme Customization Section - Admin and Manager Only */}
      {isAdminOrManager && (
        <div className="card glassCard mb-3">
          <div className="card-content">
            <div className="flex flex-align-center mb-2">
              <span className="material-icons mr-1">palette</span>
              <h3 className="h6 mb-0">App Theme Customization</h3>
            </div>
            <p className="text-secondary mb-2">
              Customize the application's default color scheme and appearance. Changes apply system-wide for all users.
            </p>
            <ThemeCustomizer />
          </div>
        </div>
      )}

      {/* Personal Theme Section - Available to ALL Users */}
      <div className="card glassCard mb-3">
        <div className="card-content">
          <div className="flex flex-align-center mb-2">
            <span className="material-icons mr-1">person</span>
            <h3 className="h6 mb-0">Personal Theme</h3>
          </div>
          <p className="text-secondary mb-2">
            Customize your personal theme or use the app's default theme.
          </p>
          <UserThemeCustomizer />
        </div>
      </div>

      {/* Background Image Section - Available to Admin and Manager */}
      {isAdminOrManager && (
        <div className="card glassCard mb-3">
          <div className="card-content">
            <div className="flex flex-align-center mb-2">
              <span className="material-icons mr-1">photo_library</span>
              <h3 className="h6 mb-0">Background Image</h3>
            </div>

          {currentBackground && (
            <div className="mb-2">
              <p className="text-secondary mb-1">Current Background:</p>
              <div
                style={{
                  width: '200px',
                  height: '100px',
                  backgroundImage: `url(${currentBackground})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius-sm)',
                }}
              />
            </div>
          )}



          <p className="text-secondary mb-2">
            Upload a new background image for the application. Supported formats: JPEG, PNG, GIF, WebP. Maximum size: 10MB.
          </p>
        </div>

        <div className="card-actions">
          <button
            className="btn btn-outlined"
            onClick={handleShowGallery}
          >
            <span className="material-icons mr-1">photo_library</span>
            Browse Gallery
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: 'none' }}
          />

          <button
            className="btn btn-contained"
            onClick={handleFileSelect}
            disabled={uploading}
          >
            <span className="material-icons mr-1">
              {uploading ? 'hourglass_empty' : 'cloud_upload'}
            </span>
            {uploading ? 'Uploading...' : 'Upload Background'}
          </button>
        </div>
        </div>
      )}

      {/* Create User Section - Available to Admin and Manager */}
      {isAdminOrManager && (
        <div className="card glassCard mb-3">
          <div className="card-content">
            <div className="flex flex-align-center mb-2">
              <span className="material-icons mr-1">person_add</span>
              <h3 className="h6 mb-0">Create New User</h3>
            </div>

            <p className="text-secondary mb-2">
              Create a new user account. A welcome email with password reset instructions will be sent.
            </p>
          </div>

        <div className="card-actions">
          <button
            className="btn btn-contained"
            onClick={handleCreateUserOpen}
          >
            <span className="material-icons mr-1">person_add</span>
            Create User
          </button>
        </div>
        </div>
      )}

      {/* CSV Bulk User Import Section - Available to Admin and Manager */}
      {isAdminOrManager && (
        <div className="card glassCard mb-3">
          <div className="card-content">
            <div className="flex flex-align-center mb-2">
              <span className="material-icons mr-1">upload_file</span>
              <h3 className="h6 mb-0">Bulk User Import</h3>
            </div>

            <p className="text-secondary mb-2">
              Upload a CSV file to create multiple user accounts at once. The CSV must include specific column headers.
            </p>

            {/* <div className="alert alert-info mb-2">
              <strong>Required CSV Columns:</strong>
              <br />
              <code>firstName, lastName</code> (at minimum)
              <br />
              <code>email, displayName, phoneNumber, occupation, bio, birthday, isBirthdayVisible</code> (optional)
              <br /><br />
              <strong>Notes:</strong>
              
            </div> */}

            {importResult && (
              <div className="alert alert-info mb-2">
                <strong>Import Results:</strong>
                <br />
                Total: {importResult.total}, Imported: {importResult.imported}, Skipped: {importResult.skipped}
                {importResult.errors.length > 0 && (
                  <div>
                    <br />
                    <strong>Errors:</strong>
                    <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                      {importResult.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card-actions">
            <input
              type="file"
              ref={csvFileInputRef}
              onChange={handleCsvFileChange}
              accept=".csv"
              style={{ display: 'none' }}
            />

            <button
              className="btn btn-text mr-2"
              onClick={handleDownloadSampleCSV}
              disabled={csvImporting}
            >
              <span className="material-icons mr-1">download</span>
              Download Sample CSV
            </button>

            <button
              className="btn btn-contained"
              onClick={handleCsvFileSelect}
              disabled={csvImporting}
            >
              <span className="material-icons mr-1">
                {csvImporting ? 'hourglass_empty' : 'upload_file'}
              </span>
              {csvImporting ? 'Importing...' : 'Upload CSV File'}
            </button>
          </div>
        </div>
      )}

      {/* Email System - Available to Admin only */}
      {isAdmin && (
        <div className="card glassCard mb-3">
          <div className="card-content">
            <div className="flex flex-align-center mb-2">
              <span className="material-icons mr-1">email</span>
              <h3 className="h6 mb-0">Email System</h3>
            </div>

            <p className="text-secondary mb-2">
              Manage email communications and test email system configuration.
            </p>

            <h4 className="h6 mb-1 mt-3">Test Email</h4>
            <p className="text-secondary mb-2" style={{ fontSize: '0.875rem' }}>
              Send a test password reset email to verify SMTP settings.
            </p>

            <div className="textfield" style={{ marginBottom: '16px' }}>
              <div className="textfield-root">
                <input
                  className="textfield-input"
                  type="email"
                  placeholder=" "
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  disabled={testingEmail}
                />
                <label className="textfield-label">Test Email Address</label>
              </div>
            </div>

            <button
              className="btn btn-contained mb-3"
              onClick={handleTestEmail}
              disabled={testingEmail || !testEmailAddress}
            >
              <span className="material-icons mr-1">
                {testingEmail ? 'hourglass_empty' : 'send'}
              </span>
              {testingEmail ? 'Sending...' : 'Send Test Email'}
            </button>

            <h4 className="h6 mb-1 mt-3">First Login Reminders</h4>
            <p className="text-secondary mb-2" style={{ fontSize: '0.875rem' }}>
              Send reminder emails to all users who have never logged in, encouraging them to set up their profile.
            </p>

            <button
              className="btn btn-contained"
              onClick={handleOpenReminderModal}
            >
              <span className="material-icons mr-1">notifications_active</span>
              Select Users for First Login Reminders
            </button>
          </div>
        </div>
      )}

      {/* Message Management Section - Available to Admin only */}
      {isAdmin && (
        <div className="card glassCard" style={{ marginBottom: '100px' }}>
          <div className="card-content">
            <div className="flex flex-align-center mb-2">
              <span className="material-icons mr-1">chat</span>
              <h3 className="h6 mb-0">Message Management</h3>
            </div>

            <p className="text-secondary mb-2">
              Configure message expiration and manage stored messages.
            </p>

            <h4 className="h6 mb-1 mt-3">Message Expiration</h4>
            <p className="text-secondary mb-2" style={{ fontSize: '0.875rem' }}>
              Set how many hours messages are stored after being read before they expire and are permanently deleted. Default is 720 hours (30 days).
            </p>

            <div className="textfield" style={{ marginBottom: '16px' }}>
              <div className="textfield-root">
                <input
                  className="textfield-input"
                  type="number"
                  min="1"
                  max="8760"
                  placeholder=" "
                  value={messageExpirationHours}
                  onChange={(e) => setMessageExpirationHours(e.target.value)}
                  disabled={savingMessageSettings}
                />
                <label className="textfield-label">Expiration Hours</label>
              </div>
            </div>

            <h4 className="h6 mb-1 mt-3">Conversation Trash Cleanup</h4>
            <p className="text-secondary mb-2" style={{ fontSize: '0.875rem' }}>
              Set how many days deleted conversations stay in trash before permanent deletion. Default is 30 days.
            </p>

            <div className="textfield" style={{ marginBottom: '16px' }}>
              <div className="textfield-root">
                <input
                  className="textfield-input"
                  type="number"
                  min="1"
                  max="365"
                  placeholder=" "
                  value={conversationTrashDays}
                  onChange={(e) => setConversationTrashDays(e.target.value)}
                  disabled={savingMessageSettings}
                />
                <label className="textfield-label">Trash Cleanup Days</label>
              </div>
            </div>

            <button
              className="btn btn-contained mb-3"
              onClick={handleSaveMessageSettings}
              disabled={savingMessageSettings || !messageExpirationHours || !conversationTrashDays}
            >
              <span className="material-icons mr-1">
                {savingMessageSettings ? 'hourglass_empty' : 'save'}
              </span>
              {savingMessageSettings ? 'Saving...' : 'Save Message Settings'}
            </button>

            <h4 className="h6 mb-1 mt-3">Conversation Trash</h4>
            <p className="text-secondary mb-2" style={{ fontSize: '0.875rem' }}>
              View and manage deleted conversations. Restore conversations or clean up old trash.
            </p>

            <button
              className="btn btn-outlined mb-3"
              onClick={handleLoadTrash}
              disabled={loadingTrash}
            >
              <span className="material-icons mr-1">
                {loadingTrash ? 'hourglass_empty' : 'delete'}
              </span>
              {loadingTrash ? 'Loading...' : 'View Trash'}
            </button>

            <h4 className="h6 mb-1 mt-3">Delete Expired Messages</h4>
            <p className="text-secondary mb-2" style={{ fontSize: '0.875rem' }}>
              Manually delete all messages that have passed their expiration date. This will permanently remove expired messages from the database.
            </p>

            <button
              className="btn btn-danger"
              onClick={handleDeleteExpiredMessages}
              disabled={deletingExpiredMessages}
              style={{ borderColor: 'var(--error-color)', color: 'var(--error-color)' }}
            >
              <span className="material-icons mr-1">
                {deletingExpiredMessages ? 'hourglass_empty' : 'delete_forever'}
              </span>
              {deletingExpiredMessages ? 'Deleting...' : 'Delete Expired Messages'}
            </button>
          </div>
        </div>
      )}

      {/* Create User Dialog - Available to Admin and Manager */}
      {isAdminOrManager && createUserDialog && (
        <>
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 2000,
            }}
            onClick={handleCreateUserClose}
          ></div>
          <div
            className="glassContainer auth-form-container"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2001,
              padding: '24px',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'radial-gradient(circle at center, rgba(181, 231, 255, 0.2), rgba(0, 0, 0, 0.63) 70%)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '1.75em', color: 'white' }}>Create New User</h2>
              <button className="icon-btn" onClick={handleCreateUserClose} style={{ color: 'white' }}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <div>
              <div className="textfield" style={{ marginBottom: '16px' }}>
                <div className="textfield-root">
                  <input
                    className="textfield-input"
                    type="email"
                    placeholder=" "
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    disabled={creatingUser}
                    required
                  />
                  <label className="textfield-label">Email *</label>
                </div>
              </div>

              <div className="textfield" style={{ marginBottom: '16px' }}>
                <div className="textfield-root">
                  <input
                    className="textfield-input"
                    type="text"
                    placeholder=" "
                    value={newUser.displayName}
                    onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                    disabled={creatingUser}
                    required
                  />
                  <label className="textfield-label">Display Name *</label>
                </div>
              </div>

              <div className="textfield" style={{ marginBottom: '16px' }}>
                <div className="textfield-root">
                  <input
                    className="textfield-input"
                    type="text"
                    placeholder=" "
                    value={newUser.firstName}
                    onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                    disabled={creatingUser}
                  />
                  <label className="textfield-label">First Name</label>
                </div>
              </div>

              <div className="textfield" style={{ marginBottom: '16px' }}>
                <div className="textfield-root">
                  <input
                    className="textfield-input"
                    type="text"
                    placeholder=" "
                    value={newUser.lastName}
                    onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                    disabled={creatingUser}
                  />
                  <label className="textfield-label">Last Name</label>
                </div>
              </div>

              <div className="textfield" style={{ marginBottom: '16px' }}>
                <div className="textfield-root">
                  <input
                    className="textfield-input"
                    type="tel"
                    placeholder=" "
                    value={newUser.phoneNumber}
                    onChange={(e) => setNewUser({ ...newUser, phoneNumber: e.target.value })}
                    disabled={creatingUser}
                  />
                  <label className="textfield-label">Phone Number</label>
                </div>
              </div>

              <div className="textfield" style={{ marginBottom: '16px' }}>
                <div className="textfield-root">
                  <input
                    className="textfield-input"
                    type="text"
                    placeholder=" "
                    value={newUser.occupation}
                    onChange={(e) => setNewUser({ ...newUser, occupation: e.target.value })}
                    disabled={creatingUser}
                  />
                  <label className="textfield-label">Occupation</label>
                </div>
              </div>

              <div className="textfield" style={{ marginBottom: '16px' }}>
                <div className="textfield-root">
                  <textarea
                    className="textfield-input"
                    placeholder=" "
                    rows={3}
                    value={newUser.bio}
                    onChange={(e) => setNewUser({ ...newUser, bio: e.target.value })}
                    disabled={creatingUser}
                    style={{ height: 'auto', minHeight: '80px' }}
                  />
                  <label className="textfield-label">Bio</label>
                </div>
              </div>

              <div className="textfield" style={{ marginBottom: '16px' }}>
                <div className="textfield-root">
                  <input
                    className="textfield-input"
                    type="url"
                    placeholder=" "
                    value={newUser.photoURL}
                    onChange={(e) => setNewUser({ ...newUser, photoURL: e.target.value })}
                    disabled={creatingUser}
                  />
                  <label className="textfield-label">Photo URL</label>
                </div>
              </div>

              <div className="textfield" style={{ marginBottom: '16px' }}>
                <div className="textfield-root">
                  <input
                    className="textfield-input"
                    type="date"
                    placeholder=" "
                    value={newUser.birthday}
                    onChange={(e) => setNewUser({ ...newUser, birthday: e.target.value })}
                    disabled={creatingUser}
                  />
                  <label className="textfield-label">Birthday</label>
                </div>
              </div>

              <div className="form-group">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={newUser.isBirthdayVisible}
                    onChange={(e) => setNewUser({ ...newUser, isBirthdayVisible: e.target.checked })}
                    disabled={creatingUser}
                  />
                  <span className="form-checkbox-label">
                    Show birthday on family calendar
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={newUser.isLocationVisible}
                    onChange={(e) => setNewUser({ ...newUser, isLocationVisible: e.target.checked })}
                    disabled={creatingUser}
                  />
                  <span className="form-checkbox-label">
                    Make location visible to other family members
                  </span>
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-text"
                onClick={handleCreateUserClose}
                disabled={creatingUser}
                style={{ color: 'white' }}
              >
                Cancel
              </button>
              <button
                className="btn btn-contained"
                onClick={handleCreateUser}
                disabled={creatingUser || !newUser.email || !newUser.displayName}
                style={{ backgroundColor: '#3a9397' }}
              >
                <span className="material-icons mr-1">
                  {creatingUser ? 'hourglass_empty' : 'person_add'}
                </span>
                {creatingUser ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Background Gallery Modal */}
      {showGallery && (
        <>
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 2000,
            }}
            onClick={() => setShowGallery(false)}
          />
          <div
            className="position-fixed glassContainer"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2001,
              width: '90vw',
              maxWidth: '800px',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '20px',
              background: 'radial-gradient(circle at center, rgba(181, 231, 255, 0.2), rgba(0, 0, 0, 0.63) 70%)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className='d-flex flex-column gap-3'
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '15px',
                borderBottom: '1px solid var(--border-color)',
              }}
            >
              <h4 style={{ margin: 0 }}>Background Gallery<button
                className="btn btn-sm"
                onClick={() => setShowGallery(false)}
                style={{ fontSize: '18px', padding: '4px 10px' }}
              >
                ✕
              </button></h4>
              
            </div>
            <div className='d-flex flex-row gap-3'>
            {loadingGallery ? (
              <div className="text-center py-4">
                <div>Loading gallery...</div>
              </div>
            ) : backgroundImages.length === 0 ? (
              <div className="text-center py-4">
                <div>No background images found. Upload some images first.</div>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '16px',
                  justifyContent: 'flex-start',
                }}
              >
                {backgroundImages.map((bg) => (
                  <div
                    key={bg.filename}
                    className="position-relative"
                    style={{
                      border: bg.isCurrent ? '3px solid var(--primary-color)' : '1px solid var(--border-color)',
                      borderRadius: 'var(--border-radius)',
                      overflow: 'hidden',
                      backgroundColor: 'var(--background-paper)',
                      width: '200px',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '120px',
                        backgroundImage: `url(${bg.url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        cursor: 'pointer',
                        backgroundColor: '#f0f0f0',
                      }}
                      onClick={() => handleSelectBackground(bg.url)}
                    />
                    <div style={{ padding: '8px', fontSize: '12px' }}>
                      <div style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>
                        {bg.isCurrent && <span style={{ color: 'var(--primary-color)', display: 'block', marginBottom: '4px' }}>★ Current</span>}
                        {formatFileSize(bg.size)}
                      </div>
                      <div style={{ marginTop: '8px', display: 'flex', gap: '4px' }}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleSelectBackground(bg.url)}
                          disabled={bg.isCurrent}
                          style={{ fontSize: '10px', padding: '2px 8px' }}
                        >
                          {bg.isCurrent ? 'Current' : 'Select'}
                        </button>
                        {!bg.isCurrent && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteBackground(bg.filePath)}
                            style={{ fontSize: '10px', padding: '2px 8px' }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        </>
      )}

      {/* User Management Modal */}
      {/* Device Management Modal */}
      {showDevicesModal && createPortal(
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9000 }}
            onClick={() => setShowDevicesModal(false)} />
          <div className="glassContainer" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 9001, width: '90vw', maxWidth: '560px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>My Devices</h3>
              <button className="btn btn-text" onClick={() => setShowDevicesModal(false)}>✕</button>
            </div>
            {loadingDevices ? (
              <p className="text-secondary">Loading...</p>
            ) : devices.length === 0 ? (
              <p className="text-secondary" style={{ textAlign: 'center', padding: '32px 0' }}>No devices registered.</p>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {devices.map((device) => (
                  <div key={device.device_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="material-icons" style={{ fontSize: '20px', opacity: 0.7 }}>
                          {device.device_type === 'mobile' ? 'phone_android' : device.device_type === 'tablet' ? 'tablet' : 'computer'}
                        </span>
                        <span style={{ fontWeight: 600 }}>{device.device_name}</span>
                        {device.is_primary && <span style={{ fontSize: '11px', background: 'var(--primary-color)', color: 'white', borderRadius: '4px', padding: '1px 6px' }}>Current</span>}
                      </div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '2px', marginLeft: '28px' }}>
                        {[device.browser, device.os].filter(Boolean).join(' · ')}
                        {device.last_seen && ` · Last seen ${new Date(device.last_seen).toLocaleDateString()}`}
                      </div>
                    </div>
                    {!device.is_primary && (
                      <button className="btn btn-outlined" style={{ borderColor: '#e74c3c', color: '#e74c3c', padding: '4px 12px', fontSize: '0.8rem', flexShrink: 0, marginLeft: '12px' }}
                        onClick={() => handleDeleteDevice(device.device_id, device.device_name)}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>,
        document.body
      )}

      {/* Pending Approvals Modal */}
      {showPendingModal && createPortal(
        <>
          <div
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9000 }}
            onClick={() => setShowPendingModal(false)}
          />
          <div
            className="glassContainer"
            style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9001, width: '90vw', maxWidth: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: '20px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Pending Member Approvals</h3>
              <button className="btn btn-text" onClick={() => setShowPendingModal(false)}>✕</button>
            </div>

            {loadingPending ? (
              <p className="text-secondary">Loading...</p>
            ) : pendingUsers.length === 0 ? (
              <p className="text-secondary" style={{ textAlign: 'center', padding: '32px 0' }}>No pending approvals.</p>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {pendingUsers.map((user) => {
                  const name = user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
                  return (
                    <div key={user.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                        <div style={{ fontSize: '0.8rem', opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.5 }}>{new Date(user.created_at as any).toLocaleDateString()}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '12px' }}>
                        <button className="btn btn-contained" style={{ backgroundColor: '#3a9397', color: 'white', padding: '6px 14px', fontSize: '0.8rem' }} onClick={() => handleApproveUser(user)}>
                          Approve
                        </button>
                        <button className="btn btn-outlined" style={{ borderColor: '#e74c3c', color: '#e74c3c', padding: '6px 14px', fontSize: '0.8rem' }} onClick={() => handleRejectUser(user)}>
                          Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>,
        document.body
      )}

      {showUserManagement && createPortal(
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 9000,
              overflow: 'hidden',
            }}
            onClick={handleCloseUserManagement}
            onWheel={(e) => e.preventDefault()}
            onTouchMove={(e) => e.preventDefault()}
          />
          <div
            className="glassContainer"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9001,
              width: '90vw',
              maxWidth: '600px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              padding: '20px',
              overflow: 'hidden',
              pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-management-header" style={{ flexShrink: 0, position: 'absolute', zIndex: 2, marginBottom: '16px', top: '10px', paddingLeft: '5%', paddingRight: '5%', padding: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h2 style={{ margin: 0, fontSize: '1.5em' }}>User Management</h2>
                <button
                  className="icon-btn"
                  onClick={handleCloseUserManagement}
                  aria-label="Close"
                >
                  <span className="material-icons">close</span>
                </button>
              </div>

              <div className="textfield textfield-with-start-adornment" style={{ margin: 0 }}>
                <div className="textfield-root">
                  <div className="input-adornment input-adornment-start">
                    <span className="material-icons">search</span>
                  </div>
                  <input
                    className="textfield-input"
                    type="text"
                    placeholder="Search users by name or email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {message && showUserManagement && (
              <div className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'} mb-2`} style={{ flexShrink: 0, position: 'relative', zIndex: 2 }}>
                {message.text}
              </div>
            )}

            {loadingUsers ? (
              <div className="text-center py-4" style={{ position: 'relative', zIndex: 2 }}>
                <div>Loading users...</div>
              </div>
            ) : allUsers.length === 0 ? (
              <div className="text-center py-4" style={{ position: 'relative', zIndex: 2 }}>
                <div>No users found.</div>
              </div>
            ) : (
              <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                top: '120px',
                minHeight: '30vh',
                WebkitOverflowScrolling: 'touch',
                position: 'relative',
                zIndex: 2,
                paddingRight: '16px'
              }}>
                {allUsers.filter((user) => {
                  if (!userSearchTerm) return true;
                  const searchLower = userSearchTerm.toLowerCase();
                  const userDisplay = user.displayName || user.display_name || `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || user.email;
                  return (
                    userDisplay.toLowerCase().includes(searchLower) ||
                    user.email.toLowerCase().includes(searchLower) ||
                    (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
                    (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
                    (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
                    (user.last_name && user.last_name.toLowerCase().includes(searchLower))
                  );
                }).map((user) => {
                  const userDisplay = user.displayName || user.display_name || `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || user.email;
                  const isCurrentUser = Number(user.id) === Number(loginUser?.id);
                  const isAdminUser = Number(user.id) === 1;
                  const isManagerUser = Number(user.id) === 2;

                  return (
                    <div
                      key={user.id}
                      className="glassCard card mb-2"
                      style={{
                        border: isCurrentUser ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                      }}
                    >
                      <div className="card-content" style={{ padding: '12px' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                              {userDisplay}
                              {isCurrentUser && <span style={{ color: 'var(--primary-color)', fontSize: '12px', marginLeft: '8px' }}>(You)</span>}
                              {isAdminUser && <span style={{ color: 'var(--warning-color)', fontSize: '12px', marginLeft: '8px' }}>(Admin)</span>}
                              {isManagerUser && !isAdminUser && <span style={{ color: 'var(--info-color)', fontSize: '12px', marginLeft: '8px' }}>(Manager)</span>}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                              {user.email}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                              ID: {user.id} | Created: {new Date(user.created_at || '').toLocaleDateString()}
                              {user.last_login && ` | Last Login: ${new Date(user.last_login).toLocaleDateString()}`}
                            </div>
                            {(user.phoneNumber || user.phone_number) && (
                              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                Phone: {user.phoneNumber || user.phone_number}
                              </div>
                            )}
                            {user.occupation && (
                              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                Occupation: {user.occupation}
                              </div>
                            )}
                          </div>
                          <div style={{ marginLeft: '16px', display: 'flex', gap: '8px', flexDirection: 'column', minWidth: '160px' }}>
                            {isAdmin && !isCurrentUser && !isAdminUser && !isManagerUser && (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handlePromoteToManager(user)}
                                style={{ fontSize: '12px', padding: '4px 8px', width: '100%', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}
                              >
                                <span className="material-icons" style={{ fontSize: '16px' }}>upgrade</span>
                                <span>Promote</span>
                              </button>
                            )}
                            {!isCurrentUser && (
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteUser(user)}
                                style={{ fontSize: '12px', padding: '4px 8px', width: '100%', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}
                              >
                                <span className="material-icons" style={{ fontSize: '16px' }}>delete</span>
                                <span>Delete</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>,
        document.body
      )}

      {/* First Login Reminder Modal */}
      {showReminderModal && (
        <div
          className="position-fixed"
          style={{
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleCloseReminderModal}
        >
            <div
              className="glassContainer"
              style={{
                width: '90%',
                maxWidth: '600px',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                margin: '20px',
                position: 'relative',
                zIndex: 10000,
              }}
              onClick={(e) => e.stopPropagation()}
            >
            <div className="card-content" style={{ flex: '0 0 auto' }}>
              <div className="flex flex-space-between flex-align-center mb-3">
                <h3 className="h6 mb-0">Send First Login Reminders</h3>
                <button
                  className="btn btn-text"
                  onClick={handleCloseReminderModal}
                  style={{ padding: '4px' }}
                >
                  <span className="material-icons">close</span>
                </button>
              </div>

              <p className="text-secondary mb-2">
                Select users to receive a reminder email to log in and set up their profile.
              </p>

              <div className="flex gap-2 mb-3">
                <button className="btn btn-outlined btn-sm" onClick={handleSelectAll}>
                  Select All
                </button>
                <button className="btn btn-outlined btn-sm" onClick={handleDeselectAll}>
                  Deselect All
                </button>
                <span className="text-secondary" style={{ marginLeft: 'auto', alignSelf: 'center' }}>
                  {selectedUserIds.size} of {neverLoggedInUsers.length} selected
                </span>
              </div>
            </div>

            <div
              className="card-content"
              style={{
                flex: '1 1 auto',
                overflowY: 'auto',
                maxHeight: 'calc(80vh - 250px)',
                borderTop: '1px solid var(--border-color)',
                borderBottom: '1px solid var(--border-color)',
                zIndex: '1',
              }}
            >
              {neverLoggedInUsers.length === 0 ? (
                <p className="text-secondary text-center py-3">
                  No users found who have never logged in.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {neverLoggedInUsers.map((user) => (
                    <label
                      key={user.id}
                      className="flex flex-align-center p-2"
                      style={{
                        cursor: 'pointer',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: selectedUserIds.has(user.id) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(user.id)}
                        onChange={() => handleToggleUser(user.id)}
                        style={{ marginRight: '12px', cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>
                          {user.displayName || user.display_name || user.firstName || user.first_name || 'Unknown'}
                        </div>
                        <div className="text-secondary" style={{ fontSize: '0.875rem' }}>
                          {user.email}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="card-actions" style={{ flex: '0 0 auto' }}>
              <button
                className="btn btn-outlined"
                onClick={handleCloseReminderModal}
              >
                Cancel
              </button>
              <button
                className="btn btn-contained"
                onClick={handleSendFirstLoginReminders}
                disabled={sendingReminders || selectedUserIds.size === 0}
              >
                <span className="material-icons mr-1">
                  {sendingReminders ? 'hourglass_empty' : 'send'}
                </span>
                {sendingReminders ? 'Sending...' : `Send to ${selectedUserIds.size} User(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trash Modal */}
      {showTrashModal && (
        <div
          className="position-fixed"
          style={{
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowTrashModal(false)}
        >
          <div
            className="glassContainer"
            style={{
              width: '90%',
              maxWidth: '800px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              margin: '20px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-content" style={{ flex: '0 0 auto' }}>
              <div className="flex flex-space-between flex-align-center mb-3">
                <h3 className="h6 mb-0">Deleted Conversations</h3>
                <button
                  className="btn btn-text"
                  onClick={() => setShowTrashModal(false)}
                  style={{ padding: '4px' }}
                >
                  <span className="material-icons">close</span>
                </button>
              </div>

              <p className="text-secondary mb-2">
                Conversations that have been deleted. You can restore them or clean up old items.
              </p>

              <button
                className="btn btn-outlined btn-sm mb-3"
                onClick={handleCleanupOldTrash}
                style={{ borderColor: 'var(--error-color)', color: 'var(--error-color)' }}
              >
                <span className="material-icons mr-1">delete_forever</span>
                Cleanup Old Trash ({conversationTrashDays}+ days)
              </button>
            </div>

            <div
              className="card-content"
              style={{
                flex: '1 1 auto',
                overflowY: 'auto',
                maxHeight: 'calc(80vh - 200px)',
                borderTop: '1px solid var(--border-color)',
              }}
            >
              {trashedConversations.length === 0 ? (
                <p className="text-secondary text-center py-3">
                  No deleted conversations in trash.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {trashedConversations.map((item: any) => (
                    <div
                      key={item.id}
                      className="card"
                      style={{ padding: '12px' }}
                    >
                      <div className="flex flex-space-between flex-align-center">
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500 }}>
                            {item.user_display_name || item.other_user_display_name || 'Unknown User'}
                          </div>
                          <div className="text-secondary" style={{ fontSize: '0.875rem' }}>
                            Deleted {new Date(item.deleted_at).toLocaleDateString()}
                          </div>
                          <div className="text-secondary" style={{ fontSize: '0.75rem' }}>
                            By: User ID {item.deleted_by}
                          </div>
                        </div>
                        <button
                          className="btn btn-outlined btn-sm"
                          onClick={() => handleRestoreConversation(item.id)}
                        >
                          <span className="material-icons mr-1" style={{ fontSize: '18px' }}>restore</span>
                          Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordChangeModal && (
        <>
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 2000,
            }}
            onClick={() => setShowPasswordChangeModal(false)}
          />
          <div
            className="glassContainer auth-form-container"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2001,
              padding: '24px',
              maxWidth: '400px',
              width: '90vw',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '1.5em', color: 'white' }}>Change Password</h2>
              <button className="icon-btn" onClick={() => setShowPasswordChangeModal(false)} style={{ color: 'white' }}>
                <span className="material-icons">close</span>
              </button>
            </div>

            {message && showPasswordChangeModal && (
              <div className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'} mb-2`}>
                {message.text}
              </div>
            )}

            <div className="textfield" style={{ marginBottom: '16px' }}>
              <div className="textfield-root">
                <input
                  className="textfield-input"
                  type="password"
                  placeholder=" "
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={changingPassword}
                />
                <label className="textfield-label">Current Password</label>
              </div>
            </div>

            <div className="textfield" style={{ marginBottom: '16px' }}>
              <div className="textfield-root">
                <input
                  className="textfield-input"
                  type="password"
                  placeholder=" "
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={changingPassword}
                />
                <label className="textfield-label">New Password</label>
              </div>
            </div>

            <div className="textfield" style={{ marginBottom: '16px' }}>
              <div className="textfield-root">
                <input
                  className="textfield-input"
                  type="password"
                  placeholder=" "
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={changingPassword}
                />
                <label className="textfield-label">Confirm New Password</label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-text"
                onClick={() => setShowPasswordChangeModal(false)}
                disabled={changingPassword}
                style={{ color: 'white' }}
              >
                Cancel
              </button>
              <button
                className="btn btn-contained"
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                style={{ backgroundColor: '#3a9397' }}
              >
                <span className="material-icons mr-1">
                  {changingPassword ? 'hourglass_empty' : 'check'}
                </span>
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};